// Phonetic mappings for letters
const phoneticSounds: { [key: string]: string } = {
  'a': 'ay', 'b': 'bee', 'c': 'see', 'd': 'dee', 'e': 'ee',
  'f': 'eff', 'g': 'gee', 'h': 'aitch', 'i': 'eye', 'j': 'jay',
  'k': 'kay', 'l': 'ell', 'm': 'em', 'n': 'en', 'o': 'oh',
  'p': 'pee', 'q': 'cue', 'r': 'arr', 's': 'ess', 't': 'tee',
  'u': 'you', 'v': 'vee', 'w': 'double-you', 'x': 'ex', 'y': 'why', 'z': 'zee'
};

// Helper function to get voice preference from localStorage
const getVoicePreference = (): 'mom' | 'dad' => {
  const savedVoice = localStorage.getItem('voicePreference');
  return (savedVoice === 'dad' || savedVoice === 'mom') ? savedVoice : 'mom';
};

// Helper function to select appropriate voice based on preference and language
const selectVoice = (voices: SpeechSynthesisVoice[], preference: 'mom' | 'dad', lang: string = 'en'): SpeechSynthesisVoice | undefined => {
  const langPrefix = lang.split('-')[0].toLowerCase();
  const langVoices = voices.filter(v => v.lang.toLowerCase().startsWith(langPrefix));
  const targetVoices = langVoices.length > 0 ? langVoices : voices;

  if (preference === 'dad') {
    // Male voice selection
    return targetVoices.find(voice =>
      voice.name.toLowerCase().includes('male') ||
      voice.name.toLowerCase().includes('man') ||
      voice.name.toLowerCase().includes('david') ||
      voice.name.toLowerCase().includes('james') ||
      voice.name.toLowerCase().includes('daniel') ||
      voice.name.toLowerCase().includes('alex') ||
      voice.name.toLowerCase().includes('tom') ||
      voice.name.toLowerCase().includes('mark')
    ) || targetVoices.find(voice => !voice.name.toLowerCase().includes('female'));
  } else {
    // Female voice selection (mom)
    return targetVoices.find(voice =>
      voice.name.toLowerCase().includes('child') ||
      voice.name.toLowerCase().includes('kids') ||
      voice.name.toLowerCase().includes('girl') ||
      voice.name.toLowerCase().includes('junior') ||
      voice.name.toLowerCase().includes('young')
    ) || targetVoices.find(voice =>
      voice.name.toLowerCase().includes('female') ||
      voice.name.toLowerCase().includes('woman') ||
      voice.name.toLowerCase().includes('samantha') ||
      voice.name.toLowerCase().includes('susan') ||
      voice.name.toLowerCase().includes('karen') ||
      voice.name.toLowerCase().includes('tessa') ||
      voice.name.toLowerCase().includes('serena') ||
      voice.name.toLowerCase().includes('zira')
    ) || targetVoices[0];
  }
};

export const speakWord = (word: string, lang: string = 'en-US'): void => {
  if ('speechSynthesis' in window) {
    // Cancel any ongoing speech
    window.speechSynthesis.cancel();
    
    const utterance = new SpeechSynthesisUtterance(word);
    utterance.lang = lang;
    
    // Configure speech settings
    utterance.rate = 0.7; // Set to 0.7 speed for better comprehension
    utterance.pitch = 1.0;
    utterance.volume = 1.0;
    
    // Get voice preference and select appropriate voice
    const voices = window.speechSynthesis.getVoices();
    const preference = getVoicePreference();
    const selectedVoice = selectVoice(voices, preference, lang);
    
    if (selectedVoice) {
      utterance.voice = selectedVoice;
    }
    
    window.speechSynthesis.speak(utterance);
  } else {
    console.warn('Speech synthesis not supported in this browser');
  }
};

export const speakLetter = (letter: string, lang: string = 'en-US'): void => {
  if ('speechSynthesis' in window) {
    // Cancel any ongoing speech
    window.speechSynthesis.cancel();
    
    let textToSpeak = letter;
    if (lang.startsWith('en')) {
      textToSpeak = phoneticSounds[letter.toLowerCase()] || letter;
    }

    const utterance = new SpeechSynthesisUtterance(textToSpeak);
    utterance.lang = lang;
    
    // Configure speech settings for letter sounds
    utterance.rate = 0.7; // Set to 0.7 speed for letter pronunciation
    utterance.pitch = 1.1; // Slightly higher pitch for letters
    utterance.volume = 1.0;
    
    // Get voice preference and select appropriate voice
    const voices = window.speechSynthesis.getVoices();
    const preference = getVoicePreference();
    const selectedVoice = selectVoice(voices, preference, lang);
    
    if (selectedVoice) {
      utterance.voice = selectedVoice;
    }
    
    window.speechSynthesis.speak(utterance);
  } else {
    console.warn('Speech synthesis not supported in this browser');
    alert('Speech synthesis is not supported in your browser');
  }
};

export const isAudioSupported = (): boolean => {
  return 'speechSynthesis' in window;
};